import React from 'react';
import I18n from 'app/i18n';
import {observer} from 'mobx-react';

import {getLinkPath, iOSDevice} from 'app/utils';
import {Auth} from './auth_popup';
import Captcha from './captcha';
import {withGoogleReCaptcha} from 'react-google-recaptcha-v3';

import logEvent from 'app/amplitude/log_event';
import {ShowPasswordEye} from 'app/components/auth/ShowPasswordEye';

import SmartLoader from 'app/components/reusable/SmartLoader';
import {toastError} from 'app/components/toasts/liteToast';
import Post from 'app/utils/Post';
import {BrowserData} from './BrowserData';
import InputError from 'app/components/deposit/InputError';
import LoginSignWith from 'app/components/auth/LoginSignWith';
import {configStore} from 'app/store/global';
import {pushAnalyticsEvent} from 'app/utils/analytics';

@observer
class Login extends React.PureComponent {
    state = {
        captcha: false,
        error: null,
        iOSDevice: iOSDevice(),
        passwordType: 'password',
        submitting: false,
        useRecaptchaV2: false
    };

    get captchaError() {
        return this.state.error && this.state.captcha;
    }

    get credentialsError() {
        return !this.captchaError && this.state.error;
    }

    get hashRedirect() {
        return window.location.hash.split(',')[1];
    }

    get firebaseToken() {
        return window.localStorage.getItem('sentFirebaseMessagingToken');
    }

    get amplitudeToken() {
        return window.localStorage.getItem('amplitude_device_id');
    }

    get userAgent() {
        return navigator.userAgent;
    }

    logIn = async e => {
        e && e.preventDefault();

        if (this.state.submitting) {
            return;
        }

        this.setState({captcha: false, error: null, submitting: true});

        const form = new FormData(e.target);

        const egb_user = {
            name: form.get('name'),
            password: form.get('password'),
            remember_me: form.get('remember_me')
        };

        let recaptchaToken = null;
        if (this.state.useRecaptchaV2) {
            recaptchaToken = form.get('g-recaptcha-response');
        } else {
            try {
                const {executeRecaptcha} = this.props.googleReCaptchaProps || {};
                recaptchaToken = executeRecaptcha ? await executeRecaptcha('login') : null;
            } catch (error) {
                toastError(I18n.t('global.error_text_captcha'));
                this.setState({submitting: false});
                return;
            }
        }

        const browserData = new BrowserData();
        const additional_info = browserData.getAllInfo();
        const formData = {
            additional_info,
            'device[browser]': this.userAgent,
            'device[id]': this.firebaseToken || this.amplitudeToken,
            egb_user,
            'g-recaptcha-response': recaptchaToken,
            use_recaptcha_v2: this.state.useRecaptchaV2
        };

        new Post({
            params: formData,
            url: '/egb_users/sign_in'
        })
            .execute()
            .then(response => {
                if (!response.ok) {
                    const UNPROCESSABLE_ENTITY = 422;

                    if (response.status === UNPROCESSABLE_ENTITY) {
                        window.location.reload();
                    }
                    return response.json()
                        .then(errData => {
                            throw errData;
                        });
                }

                return response.json();
            })
            .then(data => {
                if (data.success) {
                    logEvent('USER_AUTHORIZED');
                    pushAnalyticsEvent('login', {
                        method: 'password'
                    });
                    configStore.CURRENT_LOCALE = data.locale;
                    location.href = getLinkPath(this.props.redirectUrl || this.hashRedirect || '/play/bets');

                    return;
                }
                if (data.error || data.errors) {
                    if (data.error === I18n.t('banned_message')) {
                        this.props.changeTabToBanned();

                        return;
                    }

                    const error = data.error ? data.error : data.errors;

                    logEvent('USER_AUTHORIZATION_FAILURE', error);
                    toastError(error);
                    this.setState({captcha: data.captcha, error, submitting: false, useRecaptchaV2: data.captcha || false});
                }
            })
            .catch(errData => {
                if (errData.error || errData.errors) {
                    const error = errData.error ? errData.error : errData.errors;
                    logEvent('USER_AUTHORIZATION_FAILURE', error);
                    toastError(error);
                    this.setState({captcha: errData.captcha, error, submitting: false, useRecaptchaV2: errData.captcha || false});
                }
            })
            .finally(() => {
                this.setState({submitting: false});
            });
    };

    onKeyUp = event => Auth.clearFiledError(this, event);

    onCaptchaPass = () => this.setState({error: null, submitting: false});

    saveFormRef = x => {
        this.formRef = x;
    };

    typePassword() {
        return this.state.passwordType === 'password';
    }

    showPasswordHandler = () => {
        this.setState({passwordType: this.typePassword() ? 'text' : 'password'});
    };

    showPasswordTitle() {
        return I18n.t(`auth.${this.typePassword() ? 'show' : 'hide'}_password_label`);
    }

    showPasswordEye() {
        return this.typePassword() ? '#eye' : '#eye_crossed';
    }

    render() {
        const {error, submitting, passwordType, useRecaptchaV2} = this.state;
        const {credentialsError} = this;
        return (
            <div className="login-form-wrapper">
                <span className="form-title">{I18n.t('login_button')}</span>
                <form
                    className={error ? 'form-popup login popup-form--error' : 'form-popup login'}
                    id="sign-in"
                    onKeyUp={this.onKeyUp}
                    onSubmit={this.logIn}
                    ref={this.saveFormRef}
                >
                    {this.captchaError && <div className="popup-form__error-hint">{error}</div>}

                    <div className={`popup-form__row popup-form__wrapper ${credentialsError ? ' field-error' : ''}`}>
                        <label className="field-label" htmlFor="field-log-login">{I18n.t('nickname')}</label>
                        <div className={`field${credentialsError ? ' field-error' : ''}`}>
                            <div className="field__wrap">
                                <input
                                    autoComplete="username"
                                    className="field__input"
                                    id="field-log-login"
                                    name="name"
                                    placeholder={I18n.t('please_enter_user_nickname')}
                                />
                                {credentialsError && <InputError error={error}/>}
                            </div>
                        </div>
                    </div>

                    <div className="popup-form__row">
                        <label className="field-label" htmlFor="field-log-password">{I18n.t('password')}</label>
                        <div className={`field field__input-group${credentialsError ? ' field-error' : ''}`}>
                            <div className="field__wrap">
                                <input
                                    autoComplete="current-password"
                                    className="password optional field__input"
                                    id="field-log-password"
                                    name="password"
                                    type={passwordType}
                                    placeholder={I18n.t('password_placeholder')}
                                />
                                {credentialsError && <InputError error={error}/>}
                                <ShowPasswordEye title={this.showPasswordTitle()}
                                    onClickHandler={this.showPasswordHandler}
                                    eyeSign={this.showPasswordEye()}/>
                            </div>
                        </div>
                        <div className="div__forgot-password">
                            <a
                                className="popup-form__forgot-password"
                                href="#"
                                onClick={this.props.forgotPassword}
                            >
                                {I18n.t('user.forgotten')}
                            </a>
                        </div>
                    </div>

                    <div className="popup-form__row captcha__row">
                        {useRecaptchaV2
                            ? <div className={`popup-form__row captcha__row${this.captchaError ? ' field-error' : ''}`}>
                                <Captcha onPass={this.onCaptchaPass} reset={error}/>
                            </div>
                            : null
                        }
                    </div>

                    <input id="egb_user_remember_me" name="remember_me" type="hidden" value="1"/>
                    {submitting
                        ? <SmartLoader loaderClassName="button-adaptive login-loader"/>
                        : <div className="popup-form__submit-row">
                            <input
                                className={`button-adaptive ${submitting ? 'button-adaptive_disable' : ''}`}
                                disabled={submitting}
                                type="submit"
                                value={I18n.t('login_button')}
                            />
                        </div>
                    }
                    <LoginSignWith redirectUrl={this.props.redirectUrl}/>
                </form>
            </div>
        );
    }
}

export default withGoogleReCaptcha(Login);
